'use client'

import { memo, type ReactNode } from 'react'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { cn } from '@/lib/utils'

// baby: stepped ease: feels like CRT frames, not soft iOS fade
const PIXEL_EASE = [0.16, 1, 0.3, 1] as const

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
      delayChildren: 0.12,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18, filter: 'blur(6px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, ease: PIXEL_EASE },
  },
}

const cartridgeVariants: Variants = {
  hidden: { opacity: 0, y: -12, scale: 0.92 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: PIXEL_EASE },
  },
}

const stageVariants: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.96, rotateX: 8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: { duration: 0.75, ease: PIXEL_EASE, delay: 0.08 },
  },
}

export interface NinxlHeroEntranceProps {
  children: ReactNode
  className?: string
}

/** Orchestrates hero boot: badge → copy → CTAs stagger in */
export const NinxlHeroEntrance = memo(function NinxlHeroEntrance({
  children,
  className,
}: NinxlHeroEntranceProps) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={cn(className)}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {children}
    </motion.div>
  )
})

/** One beat in the hero boot sequence */
export const NinxlHeroBeat = memo(function NinxlHeroBeat({
  children,
  className,
  variant = 'item',
  /** Use when this beat is outside NinxlHeroEntrance */
  standalone = false,
}: {
  children: ReactNode
  className?: string
  variant?: 'item' | 'cartridge' | 'stage'
  standalone?: boolean
}) {
  const shouldReduce = useReducedMotion()
  const variants =
    variant === 'cartridge'
      ? cartridgeVariants
      : variant === 'stage'
        ? stageVariants
        : itemVariants

  if (shouldReduce) {
    return <div className={className}>{children}</div>
  }

  // baby: stage / standalone run their own timeline (not parent stagger)
  if (standalone || variant === 'stage') {
    return (
      <motion.div
        className={cn(className)}
        initial="hidden"
        animate="visible"
        variants={variants}
        style={{ transformPerspective: 1200 }}
      >
        {children}
      </motion.div>
    )
  }

  return (
    <motion.div className={cn(className)} variants={variants}>
      {children}
    </motion.div>
  )
})

/** Magnetic press on pixel CTAs: slight pull toward cursor */
export const NinxlMagneticCta = memo(function NinxlMagneticCta({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={cn('ninxl-magnetic-cta inline-flex', className)}
      whileHover={{ scale: 1.04, y: -2 }}
      whileTap={{ scale: 0.97, y: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 22 }}
    >
      {children}
    </motion.div>
  )
})
